import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@chakra-ui/react';
import LanguageService from '../services/LanguageService';
import { useLanguageContext } from '../contexts/LanguageContext';
import Logger from '../utils/logger';

export const useLanguageChanger = () => {
  const { i18n } = useTranslation();
  const toast = useToast();
  const { availableLanguages } = useLanguageContext();
  const [loadingLanguage, setLoadingLanguage] = useState<string | null>(null);

  // Get project ID from environment
  const getProjectId = () => {
    return import.meta.env.VITE_LOCALIZELY_PROJECT_ID;
  };

  // Check if language is available
  const isLanguageAvailable = (langCode: string): boolean => {
    return availableLanguages.some(lang => lang.code === langCode);
  };

  // Change language with translation download
  const changeLanguage = async (langCode: string): Promise<boolean> => {
    // Prevent multiple simultaneous changes
    if (loadingLanguage) {
      Logger.warn('Language change already in progress');
      return false;
    }

    // Skip if it's the same language
    if (langCode === i18n.language) {
      Logger.log('Language is already active:', langCode);
      return true;
    }

    // Check if language is available
    if (!isLanguageAvailable(langCode)) {
      Logger.warn(`Language ${langCode} is not available`);
      toast({
        title: "Language not available",
        description: `${langCode.toUpperCase()} is not available in this project.`,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    setLoadingLanguage(langCode);

    try {
      const projectId = getProjectId();

      if (!projectId) {
        Logger.warn('Project ID not found');
        throw new Error('Project ID not configured');
      }

      Logger.log(`🔄 Loading fresh translations for ${langCode}...`);
      
      // Always download fresh translations (no cache due to no versioning in Localizely)
      const translations = await LanguageService.downloadFreshTranslations(
        langCode,
        projectId
      );

      if (!translations) {
        throw new Error(`Failed to load translations for ${langCode}`);
      }

      // Add translations to i18next (replace existing)
      i18n.addResourceBundle(langCode, 'translation', translations, true, true);
      Logger.log(`✅ Fresh translations loaded for ${langCode}`);

      // Change the language
      await i18n.changeLanguage(langCode);

      // Save to localStorage
      localStorage.setItem('selectedLanguage', langCode);

      // Clear inactive language caches to keep only active language
      LanguageService.clearInactiveTranslationCaches(langCode);

      Logger.log(`🌍 Language changed to ${langCode}`);

      return true;
    } catch (err) {
      Logger.error(`❌ Error changing language to ${langCode}:`, err);
      
      // Show error message
      toast({
        title: "Language change failed",
        description: `Could not switch to ${langCode.toUpperCase()}. Please try again.`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });

      return false;
    } finally {
      setLoadingLanguage(null);
    }
  };

  return {
    changeLanguage,
    loadingLanguage,
    isChangingLanguage: !!loadingLanguage
  };
}; 